import React from 'react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Button } from '@/components/ds'
import { Ic } from '@/components/icons'
import { Box, BoxHeader, BoxLabel, Pill, QueryState } from '@/components/common'
import { listCampaigns, listIdentities, listLeads, listTemplates } from '@/lib/db'
import { CHART, loadDashboard, type DashboardData, type GaugeMetric } from '@/lib/dashboard'
import { processDueSendsNow } from '@/lib/api'
import { useToast } from '@/lib/toast'
import { useAsync } from '@/lib/useAsync'
import { OPEN_CAMPAIGN, OPEN_NEW_CAMPAIGN } from './CampaignsScreen'
import { OPEN_NEW_TEMPLATE } from './TemplatesScreen'
import { OPEN_LEAD_IMPORT } from './LeadsScreen'
import type { ScreenProps } from '@/App'

/**
 * The analytics dashboard.
 *
 * Every panel reads the signed-in user's own rows — the browser client is
 * RLS-scoped, so "my data" needs no filter and cannot return anyone else's.
 * All aggregation happens in src/lib/dashboard.ts; this file is layout.
 *
 * Zero states are first-class: a brand-new account has no sends, opens or
 * leads, and every card here renders a calm "nothing yet" instead of a broken
 * axis or a NaN.
 */
export function DashboardScreen({ onNavigate, narrow }: ScreenProps) {
  const toast = useToast()
  // LIVE VIEW: while the queue holds scheduled work, the aggregates silently
  // refresh every 10s (visible tab only, stale data kept) — funnel, queue
  // donut and counts move on their own during a send. Once the queue drains
  // the next poll reads scheduled=0 and the timers stop.
  const [liveWork, setLiveWork] = React.useState(false)
  const data = useAsync(() => loadDashboard(), [], {
    refreshEvery: 10000,
    refreshActive: liveWork,
  })
  const campaignList = useAsync(() => listCampaigns(), [], {
    refreshEvery: 10000,
    refreshActive: liveWork,
  })

  React.useEffect(() => {
    setLiveWork(
      (data.data?.queueByStatus ?? []).some(
        (q) => (q.status === 'scheduled' || q.status === 'paused') && q.count > 0,
      ),
    )
  }, [data.data])

  // First-run progress: each step ticks off once that entity exists.
  const setup = useAsync(async () => {
    const [identities, templates, leads] = await Promise.all([
      listIdentities().catch(() => []),
      listTemplates(),
      listLeads({ limit: 1 }),
    ])
    return { identities: identities.length, templates: templates.length, leads: leads.count }
  }, [])

  const [running, setRunning] = React.useState(false)

  async function runNow() {
    setRunning(true)
    try {
      const r = await processDueSendsNow()
      const head = r.dry_run ? `Dry run — would send ${r.would_send}` : `Sent ${r.sent}`
      const detail = [
        `${r.campaigns_considered} campaign${r.campaigns_considered === 1 ? '' : 's'} checked`,
        r.skipped ? `${r.skipped} skipped` : null,
        r.failed ? `${r.failed} failed` : null,
        r.errors[0] ?? null,
      ]
        .filter(Boolean)
        .join(' · ')
      toast(r.failed || r.errors.length ? 'error' : r.dry_run ? 'info' : 'success', head, detail)
      if (r.sample_headers) console.log('[send] composed headers:\n' + r.sample_headers)
      data.reload()
      campaignList.reload()
    } catch (e) {
      toast('error', 'Could not process the queue', e instanceof Error ? e.message : String(e))
    } finally {
      setRunning(false)
    }
  }

  const go = (flag: string | null, value: string, page: string) => {
    if (flag) sessionStorage.setItem(flag, value)
    onNavigate(page)
  }

  const d = data.data
  const rows = campaignList.data ?? []

  return (
    <QueryState loading={data.loading} error={data.error} onRetry={data.reload}>
      <div className="dash">
        {/* ---- quick actions ------------------------------------------------ */}
        <QuickActions
          running={running}
          onNewCampaign={() => go(OPEN_NEW_CAMPAIGN, '1', 'campaigns')}
          onNewTemplate={() => go(OPEN_NEW_TEMPLATE, '1', 'templates')}
          onSenders={() => go(null, '', 'identities')}
          onImport={() => go(OPEN_LEAD_IMPORT, '1', 'leads')}
          onRunNow={runNow}
        />

        {setup.data && (
          <GettingStarted
            hasSender={setup.data.identities > 0}
            hasLiveCampaign={rows.some((c) => c.status === 'active' || c.status === 'scheduled')}
            subSteps={[
              { label: 'leads', done: setup.data.leads > 0 },
              { label: 'template', done: setup.data.templates > 0 },
              { label: 'schedule', done: rows.length > 0 },
            ]}
            onGo={(page) => go(null, '', page)}
            onStartWizard={() => go(OPEN_NEW_CAMPAIGN, '1', 'campaigns')}
          />
        )}

        {d && (
          <>
            {/* ---- hero: total sent + sends vs opens ------------------------ */}
            <div className="dash-hero">
              <HeroNumber total={d.sentTotal} delta={d.wowDelta} days={d.windowDays} />
              <SendsVsOpens data={d} />
            </div>

            {/* ---- gauges --------------------------------------------------- */}
            <div className="dash-row-3">
              {d.gauges.map((g) => (
                <Gauge key={g.label} metric={g} />
              ))}
            </div>

            {/* ---- funnel · leads by campaign · top senders ------------------ */}
            <div className="dash-row-3">
              <Funnel data={d} />
              <LeadsByCampaign data={d} />
              <TopSenders data={d} />
            </div>

            {/* ---- queue · unsubscribes · opens by hour ---------------------- */}
            <div className="dash-row-3">
              <QueueDonut data={d} onOpen={() => go(null, '', 'queue')} />
              <Unsubscribes data={d} />
              <OpensByHour data={d} />
            </div>

            {/* ---- current campaigns ---------------------------------------- */}
            <CurrentCampaigns
              data={d}
              narrow={narrow}
              onOpen={(id) => go(OPEN_CAMPAIGN, id, 'campaigns')}
              onManage={() => go(null, '', 'campaigns')}
            />
          </>
        )}
      </div>
    </QueryState>
  )
}

// --- shared pieces ------------------------------------------------------------

const pct = (v: number, digits = 0) => `${(v * 100).toFixed(digits)}%`

function Panel({
  title,
  action,
  children,
  style,
}: {
  title?: React.ReactNode
  action?: React.ReactNode
  children: React.ReactNode
  style?: React.CSSProperties
}) {
  return (
    <section className="lm-panel lm-glass" style={style}>
      {title != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <h2 className="lm-panel-title" style={{ flex: 1 }}>
            {title}
          </h2>
          {action}
        </div>
      )}
      {children}
    </section>
  )
}

/** Every chart sits in one of these, so an empty account never renders an axis. */
function ChartFrame({
  height,
  empty,
  emptyLabel,
  children,
}: {
  height: number
  empty: boolean
  emptyLabel: string
  children: React.ReactElement
}) {
  if (empty) {
    return (
      <div
        style={{
          height,
          display: 'grid',
          placeItems: 'center',
          fontSize: 12.5,
          color: 'var(--text-faint)',
          textAlign: 'center',
          padding: '0 8px',
        }}
      >
        {emptyLabel}
      </div>
    )
  }
  // ResponsiveContainer is what lets every chart shrink to its card's width on
  // a phone instead of overflowing it.
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  )
}

function ChartTip({
  active,
  payload,
  label,
  suffix = '',
}: {
  active?: boolean
  payload?: { name?: string; value?: number | string; color?: string }[]
  label?: string | number
  suffix?: string
}) {
  if (!active || !payload?.length) return null
  return (
    <div
      style={{
        background: '#1d2030',
        borderRadius: 10,
        padding: '7px 10px',
        color: '#fff',
        fontSize: 12,
        boxShadow: '0 8px 20px -8px rgba(15,15,40,.6)',
      }}
    >
      {label != null && (
        <div style={{ opacity: 0.62, marginBottom: 3 }}>
          {label}
          {suffix}
        </div>
      )}
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{ width: 7, height: 7, borderRadius: '50%', background: p.color ?? CHART.indigo }}
          />
          <span style={{ opacity: 0.8 }}>{p.name}</span>
          <strong style={{ marginLeft: 'auto' }}>{p.value}</strong>
        </div>
      ))}
    </div>
  )
}

const AXIS = {
  tick: { fontSize: 11, fill: 'var(--text-faint)' },
  axisLine: false as const,
  tickLine: false as const,
}

// --- quick actions ------------------------------------------------------------

/**
 * "Process due sends now" is the manual send trigger — the only way to make the
 * queue move without waiting for the cron. It stays here.
 */
function QuickActions({
  running,
  onNewCampaign,
  onNewTemplate,
  onSenders,
  onImport,
  onRunNow,
}: {
  running: boolean
  onNewCampaign: () => void
  onNewTemplate: () => void
  onSenders: () => void
  onImport: () => void
  onRunNow: () => void
}) {
  return (
    <div
      className="lm-panel lm-glass"
      style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
    >
      <Button icon={<Ic.plus size={15} />} onClick={onNewCampaign}>
        Start new campaign
      </Button>
      <Button variant="secondary" icon={<Ic.template size={15} />} onClick={onNewTemplate}>
        Create template
      </Button>
      <Button variant="secondary" icon={<Ic.atSign size={15} />} onClick={onSenders}>
        Senders
      </Button>
      <Button variant="secondary" icon={<Ic.upload size={15} />} onClick={onImport}>
        Import leads
      </Button>
      <Button
        variant="secondary"
        icon={running ? <Ic.spinner size={15} className="animate-spin" /> : <Ic.send size={15} />}
        onClick={onRunNow}
        disabled={running}
      >
        {running ? 'Processing…' : 'Process due sends now'}
      </Button>
    </div>
  )
}

// --- hero ---------------------------------------------------------------------

function HeroNumber({
  total,
  delta,
  days,
}: {
  total: number
  delta: number | null
  days: number
}) {
  const up = (delta ?? 0) >= 0
  return (
    <Panel style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <div className="lm-panel-title">Emails sent · last {days} days</div>
      <div
        className="tnum"
        style={{
          fontSize: 'clamp(34px, 9vw, 52px)',
          fontWeight: 700,
          lineHeight: 1.05,
          letterSpacing: '-0.02em',
          color: 'var(--text-primary)',
          margin: '6px 0 8px',
        }}
      >
        {total.toLocaleString()}
      </div>
      {delta === null ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>
          No prior week to compare against
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          <span
            className="tnum"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 3,
              fontSize: 12.5,
              fontWeight: 700,
              borderRadius: 999,
              padding: '2px 9px',
              background: up ? 'var(--status-approved-bg)' : 'var(--status-failed-bg)',
              color: up ? 'var(--status-approved-fg)' : 'var(--status-failed-fg)',
            }}
          >
            {up ? '↑' : '↓'} {pct(Math.abs(delta))}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>vs previous 7 days</span>
        </div>
      )}
    </Panel>
  )
}

function SendsVsOpens({ data }: { data: DashboardData }) {
  const empty = data.series.every((b) => b.sent === 0 && b.opens === 0)
  return (
    <Panel title="Sends vs opens">
      <ChartFrame
        height={190}
        empty={empty}
        emptyLabel="No sends yet — start a campaign and this fills in daily."
      >
        <AreaChart data={data.series} margin={{ top: 4, right: 6, bottom: 0, left: -22 }}>
          <defs>
            <linearGradient id="lm-sent" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART.indigo} stopOpacity={0.5} />
              <stop offset="100%" stopColor={CHART.indigo} stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="lm-opens" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={CHART.pink} stopOpacity={0.45} />
              <stop offset="100%" stopColor={CHART.pink} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis dataKey="label" {...AXIS} interval="preserveStartEnd" minTickGap={18} />
          <YAxis {...AXIS} width={40} allowDecimals={false} />
          <RTooltip content={<ChartTip />} cursor={{ stroke: 'var(--border-default)' }} />
          {/* isAnimationActive={false} on every series: Recharts replays its
              entry animation on each ResponsiveContainer resize, so a series
              flickers from opacity 0 every time the card is re-measured. A
              dashboard has no reason to animate, and holding still also makes
              the charts deterministic to screenshot. */}
          <Area
            type="monotone"
            dataKey="sent"
            name="Sent"
            stroke={CHART.indigo}
            strokeWidth={2}
            fill="url(#lm-sent)"
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="opens"
            name="Opens"
            stroke={CHART.pink}
            strokeWidth={2}
            fill="url(#lm-opens)"
            isAnimationActive={false}
          />
        </AreaChart>
      </ChartFrame>
      <Legend
        items={[
          { label: 'Sent', color: CHART.indigo },
          { label: 'Opens', color: CHART.pink },
        ]}
      />
    </Panel>
  )
}

function Legend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 6 }}>
      {items.map((i) => (
        <span
          key={i.label}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: 'var(--text-muted)',
          }}
        >
          <span
            style={{ width: 8, height: 8, borderRadius: '50%', background: i.color, flex: 'none' }}
          />
          {i.label}
        </span>
      ))}
    </div>
  )
}

// --- gauges -------------------------------------------------------------------

/**
 * The number is an HTML overlay rather than SVG text: it stays at a fixed,
 * legible size while the ring itself scales with the card, so nothing clips at
 * 360px.
 */
function Gauge({ metric }: { metric: GaugeMetric }) {
  const value = Math.min(1, Math.max(0, metric.value))
  const chartData = [{ name: metric.label, value: value * 100, fill: metric.color }]
  const hasBase = metric.denominator > 0

  return (
    <Panel title={metric.label}>
      <div style={{ position: 'relative', width: '100%', height: 150 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            innerRadius="72%"
            outerRadius="100%"
            data={chartData}
            startAngle={90}
            endAngle={-270}
            barSize={13}
          >
            <YAxis type="number" domain={[0, 100]} tick={false} axisLine={false} width={0} />
            <RadialBar
              background={{ fill: 'var(--surface-sunken)' }}
              dataKey="value"
              cornerRadius={999}
              isAnimationActive={false}
            />
          </RadialBarChart>
        </ResponsiveContainer>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div
              className="tnum"
              style={{
                fontSize: 26,
                fontWeight: 700,
                lineHeight: 1,
                color: 'var(--text-primary)',
              }}
            >
              {hasBase ? pct(metric.value, metric.value < 0.1 ? 1 : 0) : '—'}
            </div>
            <div className="tnum" style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 3 }}>
              {metric.numerator.toLocaleString()} / {metric.denominator.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', fontSize: 12, marginTop: 2 }}>
        {metric.delta === null ? (
          <span style={{ color: 'var(--text-faint)' }}>no prior period</span>
        ) : (
          <span
            style={{
              color:
                metric.delta === 0
                  ? 'var(--text-muted)'
                  : metric.delta > 0
                    ? 'var(--status-approved-fg)'
                    : 'var(--status-failed-fg)',
              fontWeight: 600,
            }}
          >
            {metric.delta > 0 ? '↑' : metric.delta < 0 ? '↓' : '·'}{' '}
            {Math.abs(metric.delta * 100).toFixed(1)} pts vs prior period
          </span>
        )}
      </div>
    </Panel>
  )
}

// --- funnel -------------------------------------------------------------------

/** Sent → Opened → Clicked. No "Replied": nothing in the schema tracks replies. */
function Funnel({ data }: { data: DashboardData }) {
  const max = data.funnel[0]?.value ?? 0
  return (
    <Panel title="Funnel">
      {max === 0 ? (
        <div style={{ height: 150, display: 'grid', placeItems: 'center', fontSize: 12.5, color: 'var(--text-faint)' }}>
          Nothing sent in the last {data.windowDays} days.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 4 }}>
          {data.funnel.map((step) => (
            <div key={step.label}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  marginBottom: 4,
                  fontSize: 12.5,
                }}
              >
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{step.label}</span>
                <span className="tnum" style={{ marginLeft: 'auto', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {step.value.toLocaleString()}
                </span>
                <span className="tnum" style={{ color: 'var(--text-muted)', minWidth: 42, textAlign: 'right' }}>
                  {pct(step.pct)}
                </span>
              </div>
              <div style={{ height: 10, borderRadius: 999, background: 'var(--surface-sunken)', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${Math.max(step.pct * 100, step.value > 0 ? 2 : 0)}%`,
                    height: '100%',
                    borderRadius: 999,
                    background: step.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  )
}

// --- leads by campaign --------------------------------------------------------

function LeadsByCampaign({ data }: { data: DashboardData }) {
  const rows = data.leadsByCampaign
  return (
    <Panel
      title="Leads by campaign"
      action={
        <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {data.leadsTotal.toLocaleString()} total
        </span>
      }
    >
      <ChartFrame
        height={Math.max(150, rows.length * 30)}
        empty={rows.length === 0}
        emptyLabel="No leads imported yet."
      >
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 0, right: 10, bottom: 0, left: 0 }}
          barCategoryGap={7}
        >
          <XAxis type="number" hide allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="name"
            {...AXIS}
            width={92}
            tickFormatter={(v: string) => (v.length > 13 ? `${v.slice(0, 12)}…` : v)}
          />
          <RTooltip content={<ChartTip />} cursor={{ fill: 'rgba(91,91,214,.07)' }} />
          <Bar dataKey="leads" name="Leads" radius={[0, 6, 6, 0]} fill={CHART.indigo} isAnimationActive={false} />
        </BarChart>
      </ChartFrame>
    </Panel>
  )
}

// --- top senders --------------------------------------------------------------

function TopSenders({ data }: { data: DashboardData }) {
  return (
    <Panel title="Top senders">
      {data.topSenders.length === 0 ? (
        <div style={{ height: 150, display: 'grid', placeItems: 'center', fontSize: 12.5, color: 'var(--text-faint)', textAlign: 'center' }}>
          No delivered mail yet — senders appear once a campaign sends.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 2 }}>
          {data.topSenders.map((s, i) => (
            <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <span
                className="tnum"
                style={{
                  width: 20,
                  height: 20,
                  flex: 'none',
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 11,
                  fontWeight: 700,
                  background: i === 0 ? 'var(--accent)' : 'var(--accent-soft)',
                  color: i === 0 ? 'var(--text-on-accent)' : 'var(--accent-text)',
                }}
              >
                {i + 1}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s.label}
                </div>
                <div className="tnum" style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                  {s.sent.toLocaleString()} sent · {pct(s.openRate)} open
                </div>
              </div>
              <div style={{ width: 54, flex: 'none' }}>
                <div style={{ height: 6, borderRadius: 999, background: 'var(--surface-sunken)', overflow: 'hidden' }}>
                  <div
                    style={{
                      width: `${Math.min(100, s.openRate * 100)}%`,
                      height: '100%',
                      borderRadius: 999,
                      background: CHART.green,
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  )
}

// --- queue donut --------------------------------------------------------------

function QueueDonut({ data, onOpen }: { data: DashboardData; onOpen: () => void }) {
  return (
    <Panel
      title="Queue status"
      action={
        <button
          type="button"
          className="tap"
          onClick={onOpen}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
            fontSize: 12,
            color: 'var(--accent-text)',
          }}
        >
          Open queue
        </button>
      }
    >
      <div style={{ position: 'relative' }}>
        <ChartFrame height={150} empty={data.queueTotal === 0} emptyLabel="The queue is empty.">
          <PieChart>
            <RTooltip content={<ChartTip />} />
            <Pie
              data={data.queueByStatus}
              dataKey="count"
              nameKey="status"
              innerRadius="62%"
              outerRadius="92%"
              paddingAngle={2}
              stroke="none"
              isAnimationActive={false}
            >
              {data.queueByStatus.map((s) => (
                <Cell key={s.status} fill={s.color} />
              ))}
            </Pie>
          </PieChart>
        </ChartFrame>
        {data.queueTotal > 0 && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              pointerEvents: 'none',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div className="tnum" style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>
                {data.queueTotal.toLocaleString()}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>rows</div>
            </div>
          </div>
        )}
      </div>
      {data.queueByStatus.length > 0 && (
        <Legend items={data.queueByStatus.map((s) => ({ label: `${s.status} (${s.count})`, color: s.color }))} />
      )}
    </Panel>
  )
}

// --- unsubscribes -------------------------------------------------------------

function Unsubscribes({ data }: { data: DashboardData }) {
  const empty = data.unsubs.total === 0
  return (
    <Panel title={`Unsubscribes · ${data.windowDays}d`}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 }}>
        <span className="tnum" style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.1 }}>
          {data.unsubs.total.toLocaleString()}
        </span>
        <span className="tnum" style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {pct(data.unsubs.pctOfSent, 2)} of sent
        </span>
      </div>
      <ChartFrame height={96} empty={empty} emptyLabel="Nobody has opted out. ">
        <AreaChart data={data.unsubs.series} margin={{ top: 8, right: 4, bottom: 0, left: 4 }}>
          <RTooltip content={<ChartTip />} />
          <Area
            type="monotone"
            dataKey="count"
            name="Unsubscribes"
            stroke={CHART.amber}
            strokeWidth={2}
            fill={CHART.amber}
            fillOpacity={0.16}
            isAnimationActive={false}
          />
        </AreaChart>
      </ChartFrame>
    </Panel>
  )
}

// --- opens by hour ------------------------------------------------------------

function OpensByHour({ data }: { data: DashboardData }) {
  const empty = data.peakHours.length === 0
  const peak = new Set(data.peakHours)
  return (
    <Panel
      title="Opens by hour"
      action={
        !empty && (
          <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            peak {data.peakHours.map((h) => `${String(h).padStart(2, '0')}:00`).join(', ')}
          </span>
        )
      }
    >
      <ChartFrame height={150} empty={empty} emptyLabel="No opens recorded yet.">
        <BarChart data={data.opensByHour} margin={{ top: 4, right: 2, bottom: 0, left: -26 }}>
          <XAxis dataKey="label" {...AXIS} interval={3} />
          <YAxis {...AXIS} width={38} allowDecimals={false} />
          <RTooltip content={<ChartTip suffix=":00" />} cursor={{ fill: 'rgba(91,91,214,.07)' }} />
          <Bar dataKey="opens" name="Opens" radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {data.opensByHour.map((h) => (
              <Cell key={h.hour} fill={peak.has(h.hour) ? CHART.amber : CHART.indigoLight} />
            ))}
          </Bar>
        </BarChart>
      </ChartFrame>
    </Panel>
  )
}

// --- current campaigns --------------------------------------------------------

function Ring({ value, size = 34 }: { value: number; size?: number }) {
  const r = (size - 5) / 2
  const c = 2 * Math.PI * r
  const filled = Math.min(1, Math.max(0, value))
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flex: 'none' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-sunken)" strokeWidth={5} />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={CHART.indigo}
        strokeWidth={5}
        strokeLinecap="round"
        strokeDasharray={`${c * filled} ${c}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  )
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, { bg: string; fg: string }> = {
    active: { bg: 'var(--status-approved-bg)', fg: 'var(--status-approved-fg)' },
    scheduled: { bg: 'var(--status-scheduled-bg)', fg: 'var(--status-scheduled-fg)' },
    paused: { bg: 'var(--pill-neutral-bg)', fg: 'var(--text-faint)' },
  }
  const c = map[status] ?? map.paused
  return (
    <Pill bg={c.bg} fg={c.fg}>
      {status}
    </Pill>
  )
}

function CurrentCampaigns({
  data,
  narrow,
  onOpen,
  onManage,
}: {
  data: DashboardData
  narrow: boolean
  onOpen: (id: string) => void
  onManage: () => void
}) {
  return (
    <Box>
      <BoxHeader>
        <BoxLabel style={{ flex: 1 }}>Current campaigns</BoxLabel>
        <button
          type="button"
          className="tap"
          onClick={onManage}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
            fontSize: 12.5,
            color: 'var(--accent-text)',
          }}
        >
          Manage all
        </button>
      </BoxHeader>

      {data.campaigns.length === 0 ? (
        <div style={{ padding: '14px 14px', fontSize: 13, color: 'var(--text-muted)' }}>
          No active campaigns — start one from the buttons above.
        </div>
      ) : (
        data.campaigns.map((c, i) => (
          <div
            key={c.id}
            onClick={() => onOpen(c.id)}
            className="tap"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: narrow ? 'wrap' : 'nowrap',
              padding: '11px 14px',
              borderBottom: i === data.campaigns.length - 1 ? 'none' : '1px solid var(--border-row)',
              cursor: 'pointer',
            }}
          >
            <Ring value={c.total > 0 ? c.sent / c.total : 0} />
            <div style={{ minWidth: 0, flex: narrow ? '1 1 60%' : '1 1 auto' }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {c.name}
              </div>
              <div className="tnum" style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                {c.sent.toLocaleString()} of {c.total.toLocaleString()} leads
              </div>
            </div>
            <StatusPill status={c.status} />
            <div
              style={{
                display: 'flex',
                gap: 16,
                marginLeft: narrow ? 0 : 'auto',
                flex: narrow ? '1 1 100%' : 'none',
              }}
            >
              <MiniStat label="sent" value={c.sent.toLocaleString()} />
              <MiniStat label="open" value={pct(c.openRate)} />
              <MiniStat label="click" value={pct(c.clickRate)} />
            </div>
          </div>
        ))
      )}
    </Box>
  )
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ minWidth: 44 }}>
      <div className="tnum" style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>
        {value}
      </div>
      <div style={{ fontSize: 10.5, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '.05em' }}>
        {label}
      </div>
    </div>
  )
}

// --- onboarding (unchanged behaviour) ----------------------------------------

/**
 * A mini mirror of the wizard: connect a sender, then build & send. Reflects
 * real state and hides itself entirely once both are done — it is onboarding,
 * not permanent furniture.
 */
function GettingStarted({
  hasSender,
  hasLiveCampaign,
  subSteps,
  onGo,
  onStartWizard,
}: {
  hasSender: boolean
  hasLiveCampaign: boolean
  subSteps: { label: string; done: boolean }[]
  onGo: (page: string) => void
  onStartWizard: () => void
}) {
  if (hasSender && hasLiveCampaign) return null
  const doneCount = [hasSender, hasLiveCampaign].filter(Boolean).length

  return (
    <Box>
      <BoxHeader>
        <BoxLabel style={{ flex: 1 }}>Getting started</BoxLabel>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{doneCount} of 2 done</span>
      </BoxHeader>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <SetupStep
          n={1}
          done={hasSender}
          title="Connect a sender"
          hint="An SMTP mailbox to deliver from"
          action={
            <Button size="sm" variant="secondary" onClick={() => onGo('identities')}>
              {hasSender ? 'Manage' : 'Add sender'}
            </Button>
          }
        />
        <SetupStep
          n={2}
          done={hasLiveCampaign}
          last
          title="Build & send your first campaign"
          hint={
            <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap' }}>
              {subSteps.map((s) => (
                <span
                  key={s.label}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    color: s.done ? 'var(--status-approved-fg)' : 'var(--text-faint)',
                  }}
                >
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: s.done ? 'var(--status-approved-dot)' : 'var(--text-faint)',
                    }}
                  />
                  {s.label}
                </span>
              ))}
            </span>
          }
          action={
            <Button
              size="sm"
              disabled={!hasSender}
              title={hasSender ? undefined : 'Connect a sender first'}
              onClick={onStartWizard}
            >
              {hasLiveCampaign ? 'New campaign' : 'Start'}
            </Button>
          }
        />
      </div>
    </Box>
  )
}

function SetupStep({
  n,
  done,
  title,
  hint,
  action,
  last,
}: {
  n: number
  done: boolean
  title: string
  hint: React.ReactNode
  action: React.ReactNode
  last?: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 12px',
        borderBottom: last ? 'none' : '1px solid var(--border-row)',
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          flex: 'none',
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 700,
          background: done ? 'var(--status-approved-bg)' : 'var(--accent-soft)',
          color: done ? 'var(--status-approved-fg)' : 'var(--accent-text)',
        }}
      >
        {done ? <Ic.check size={12} /> : n}
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          {title}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)' }}>{hint}</span>
      </span>
      <div style={{ flex: 1 }} />
      {action}
    </div>
  )
}
